// src/pages/ResultsPage.js
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";

import ResultCard from "../components/common/ResultCard";
import CompletedActivityCardSkeleton from "../components/common/CompletedActivityCardSkeleton";
import ResultsListSkeleton, {
  RESULTS_LOADING_SKELETON_COUNT,
} from "../components/results/ResultsListSkeleton";
import ResultsModeFilterButton from "../components/results/ResultsModeFilterButton";
import ResultsTrendCardsSection from "../components/results/ResultsTrendCardsSection";
import RecordingModeTiles from "../components/recording/RecordingModeTiles";
import ProtocolTaskList from "../components/tasks/ProtocolTaskList";
import { buildResultListItem } from "../components/results/resultListItem";
import RegionalSupportCodeNotice from "../components/backend/RegionalSupportCodeNotice";
import { getResultModeLabel } from "../utils/results/resultModePresentation";
import { getRegionalSupportCode } from "../utils/backend/regionalSupportCodes";
import { buildResultsTrendCardModels } from "../utils/results/resultsTrendCards";
import { useDelayedVisibility } from "../hooks/useDelayedVisibility";
import { useRegionalMeasurementResults } from "../hooks/results/useRegionalMeasurementResults";
import { RESULT_MODE_CONFIG } from "../utils/results/resultModeConfig";
import { useUserWebAppPresentationFeatures } from "../hooks/useUserWebAppPresentationFeatures";

const RESULTS_PAGE_SIZE = 20;
const RESULTS_SCROLL_LOAD_MORE_THRESHOLD_PX = 160;
const RESULTS_TREND_LOOKBACK_MS = 30 * 24 * 60 * 60 * 1000;
const RESULTS_TREND_RESULTS_PER_VARIANT = 120;
const RESULTS_TREND_SELECTION_STORAGE_PREFIX = "pdx.results.trendMetrics";
const RESULTS_TREND_METRIC_FEATURE_KEYS = Object.freeze([
  "hr",
  "rr",
  "hrv",
  "fev1",
  "fvc",
  "pef",
  "fev1Fvc",
  "relativeChange",
]);
const RESULT_MODE_ORDER = [
  RESULT_MODE_CONFIG.spirometry,
  RESULT_MODE_CONFIG.cardiopulmonary,
  RESULT_MODE_CONFIG.manualAudio,
  RESULT_MODE_CONFIG.postExertion,
  RESULT_MODE_CONFIG.sixMinuteWalk,
];

function buildResultsTrendSelectionStorageKey(uid) {
  const normalizedUid = String(uid || "").trim();
  return normalizedUid
    ? `${RESULTS_TREND_SELECTION_STORAGE_PREFIX}:${normalizedUid}`
    : null;
}

function readStoredResultsTrendMetricKeys(uid) {
  if (typeof window === "undefined") {
    return null;
  }

  const storageKey = buildResultsTrendSelectionStorageKey(uid);

  if (!storageKey) {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);

    if (!Array.isArray(parsedValue)) {
      return null;
    }

    return parsedValue
      .map((value) => String(value || "").trim())
      .filter(Boolean);
  } catch (_error) {
    return null;
  }
}

function ResultsPage({
  user,
  isPermitted,
  loadingModes,
  firstRunByVariant = {},
  recordingAccessError = null,
  taskBoard = null,
}) {
  const { t, i18n } = useTranslation();
  const [pageSize, setPageSize] = useState(RESULTS_PAGE_SIZE);
  const [loadMoreRetryKey, setLoadMoreRetryKey] = useState(0);
  const [lastScrollLoadRowsLength, setLastScrollLoadRowsLength] =
    useState(null);
  const [selectedResultVariants, setSelectedResultVariants] = useState([]);
  const [selectedTrendMetricKeys, setSelectedTrendMetricKeys] = useState(() =>
    readStoredResultsTrendMetricKeys(user?.uid),
  );
  const skipNextTrendMetricPersistRef = useRef(true);
  const { resultFeatures = {}, qualityStatusPillsEnabled = false } =
    useUserWebAppPresentationFeatures();
  const showLoadingMessage = useDelayedVisibility(loadingModes);

  useEffect(() => {
    trackEvent("results_view", { lang: i18n.language || null });
  }, [i18n.language]);

  const permittedVariants = useMemo(() => {
    const canAccessMode =
      typeof isPermitted === "function" ? isPermitted : () => false;

    return RESULT_MODE_ORDER.filter((mode) => canAccessMode(mode.mode)).map(
      (mode) => mode.variant,
    );
  }, [isPermitted]);
  const permittedResultModes = useMemo(
    () =>
      RESULT_MODE_ORDER.filter((mode) =>
        permittedVariants.includes(mode.variant),
      ).map((mode) => ({
        variant: mode.variant,
        label: getResultModeLabel(mode.variant, t),
      })),
    [permittedVariants, t],
  );
  const selectedPermittedVariants = useMemo(
    () =>
      selectedResultVariants.filter((variant) =>
        permittedVariants.includes(variant),
      ),
    [permittedVariants, selectedResultVariants],
  );
  const filteredVariants = selectedPermittedVariants.length
    ? selectedPermittedVariants
    : permittedVariants;
  const variantKey = filteredVariants.join("|");
  const permittedVariantKey = permittedVariants.join("|");

  useEffect(() => {
    setPageSize(RESULTS_PAGE_SIZE);
    setLoadMoreRetryKey(0);
    setLastScrollLoadRowsLength(null);
  }, [variantKey]);

  useEffect(() => {
    setSelectedResultVariants((currentVariants) => {
      const nextVariants = currentVariants.filter((variant) =>
        permittedVariants.includes(variant),
      );

      return nextVariants.length === currentVariants.length
        ? currentVariants
        : nextVariants;
    });
  }, [permittedVariantKey, permittedVariants]);
  useEffect(() => {
    skipNextTrendMetricPersistRef.current = true;
    setSelectedTrendMetricKeys(readStoredResultsTrendMetricKeys(user?.uid));
  }, [user?.uid]);
  useEffect(() => {
    if (skipNextTrendMetricPersistRef.current) {
      skipNextTrendMetricPersistRef.current = false;
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const storageKey = buildResultsTrendSelectionStorageKey(user?.uid);

    if (!storageKey) {
      return;
    }

    try {
      if (!Array.isArray(selectedTrendMetricKeys)) {
        window.localStorage.removeItem(storageKey);
        return;
      }

      const persistedMetricKeys = Array.from(
        new Set(
          selectedTrendMetricKeys
            .map((metricKey) => String(metricKey || "").trim())
            .filter(Boolean),
        ),
      );

      window.localStorage.setItem(
        storageKey,
        JSON.stringify(persistedMetricKeys),
      );
    } catch (_error) {
      // Storage is best-effort only.
    }
  }, [selectedTrendMetricKeys, user?.uid]);

  const handleToggleResultVariant = useCallback(
    (variant) => {
      setSelectedResultVariants((currentVariants) => {
        const activeVariants = currentVariants.length
          ? currentVariants
          : permittedVariants;
        const nextVariants = activeVariants.includes(variant)
          ? activeVariants.filter(
              (currentVariant) => currentVariant !== variant,
            )
          : [...activeVariants, variant];

        if (
          nextVariants.length === 0 ||
          nextVariants.length === permittedVariants.length
        ) {
          return [];
        }

        return permittedVariants.filter((permittedVariant) =>
          nextVariants.includes(permittedVariant),
        );
      });
    },
    [permittedVariants],
  );
  const handleClearResultFilters = useCallback(() => {
    setSelectedResultVariants([]);
  }, []);
  const hasEnabledTrendMetricFeatures = useMemo(
    () =>
      RESULTS_TREND_METRIC_FEATURE_KEYS.some((featureKey) =>
        Boolean(resultFeatures[featureKey]),
      ),
    [resultFeatures],
  );

  const measurementResults = useRegionalMeasurementResults({
    uid: loadingModes ? null : user?.uid,
    variants: filteredVariants,
    pageSize,
    refreshKey: loadMoreRetryKey,
    languageFallback: i18n.resolvedLanguage || i18n.language || "en-GB",
  });
  const trendMeasurementResults = useRegionalMeasurementResults({
    uid:
      loadingModes ||
      !resultFeatures.resultsTrends ||
      !hasEnabledTrendMetricFeatures
        ? null
        : user?.uid,
    variants:
      resultFeatures.resultsTrends && hasEnabledTrendMetricFeatures
        ? permittedVariants
        : [],
    pageSize: permittedVariants.length * RESULTS_TREND_RESULTS_PER_VARIANT,
    status: "complete",
    perVariantLimit: RESULTS_TREND_RESULTS_PER_VARIANT,
    languageFallback: i18n.resolvedLanguage || i18n.language || "en-GB",
  });
  const rows = useMemo(
    () =>
      (measurementResults.rows || [])
        .map((row) =>
          buildResultListItem(
            row,
            t,
            resultFeatures,
            qualityStatusPillsEnabled,
          ),
        )
        .filter(Boolean),
    [measurementResults.rows, qualityStatusPillsEnabled, resultFeatures, t],
  );
  const showInitialLoading = measurementResults.loading && rows.length === 0;
  const showListError = Boolean(measurementResults.error) && rows.length === 0;
  const showLoadMoreError =
    Boolean(measurementResults.error) && rows.length > 0;
  const hasActiveResultFilters =
    selectedPermittedVariants.length > 0 &&
    selectedPermittedVariants.length < permittedVariants.length;
  const showHistoryEmptyState =
    !showInitialLoading && !measurementResults.error && rows.length === 0;
  const showEmpty = showHistoryEmptyState && !hasActiveResultFilters;
  const showFilteredEmpty = showHistoryEmptyState && hasActiveResultFilters;
  const trendCards = useMemo(() => {
    if (!resultFeatures.resultsTrends || !hasEnabledTrendMetricFeatures) {
      return [];
    }

    return buildResultsTrendCardModels({
      rows: trendMeasurementResults.rows,
      language: i18n.resolvedLanguage || i18n.language || "en-GB",
      t,
      trendStartTimestamp: Date.now() - RESULTS_TREND_LOOKBACK_MS,
    }).filter((card) => Boolean(resultFeatures[card.featureKey]));
  }, [
    i18n.language,
    i18n.resolvedLanguage,
    hasEnabledTrendMetricFeatures,
    resultFeatures,
    t,
    trendMeasurementResults.rows,
  ]);
  const trendMetricOptions = useMemo(
    () => trendCards.map((card) => ({ key: card.key, label: card.label })),
    [trendCards],
  );
  const availableTrendMetricKeys = useMemo(
    () => trendMetricOptions.map((metric) => metric.key),
    [trendMetricOptions],
  );
  const selectedVisibleTrendMetricKeys = useMemo(() => {
    if (!availableTrendMetricKeys.length) {
      return [];
    }

    if (!Array.isArray(selectedTrendMetricKeys)) {
      return availableTrendMetricKeys;
    }

    return availableTrendMetricKeys.filter((metricKey) =>
      selectedTrendMetricKeys.includes(metricKey),
    );
  }, [availableTrendMetricKeys, selectedTrendMetricKeys]);
  const hasCustomTrendSelection = useMemo(() => {
    if (!Array.isArray(selectedTrendMetricKeys)) {
      return false;
    }

    if (
      selectedVisibleTrendMetricKeys.length !== availableTrendMetricKeys.length
    ) {
      return true;
    }

    return selectedVisibleTrendMetricKeys.some(
      (metricKey, index) => metricKey !== availableTrendMetricKeys[index],
    );
  }, [
    availableTrendMetricKeys,
    selectedTrendMetricKeys,
    selectedVisibleTrendMetricKeys,
  ]);
  const trendChartDomain = {
    min: Date.now() - RESULTS_TREND_LOOKBACK_MS,
    max: Date.now(),
  };
  const showTrendCardsSection =
    Boolean(resultFeatures.resultsTrends) &&
    hasEnabledTrendMetricFeatures &&
    !showEmpty &&
    (trendMeasurementResults.loading ||
      Boolean(trendMeasurementResults.error) ||
      trendMetricOptions.length > 0);
  const handleToggleTrendMetric = useCallback(
    (metricKey) => {
      setSelectedTrendMetricKeys((currentMetricKeys) => {
        const availableKeys = availableTrendMetricKeys;
        const preservedUnavailableKeys = Array.isArray(currentMetricKeys)
          ? currentMetricKeys.filter(
              (currentMetricKey) => !availableKeys.includes(currentMetricKey),
            )
          : [];
        const visibleSelectedKeys = Array.isArray(currentMetricKeys)
          ? availableKeys.filter((availableKey) =>
              currentMetricKeys.includes(availableKey),
            )
          : availableKeys;
        const nextVisibleKeys = visibleSelectedKeys.includes(metricKey)
          ? visibleSelectedKeys.filter(
              (selectedMetricKey) => selectedMetricKey !== metricKey,
            )
          : availableKeys.filter(
              (availableKey) =>
                visibleSelectedKeys.includes(availableKey) ||
                availableKey === metricKey,
            );

        if (
          nextVisibleKeys.length === availableKeys.length &&
          preservedUnavailableKeys.length === 0
        ) {
          return null;
        }

        return [...nextVisibleKeys, ...preservedUnavailableKeys];
      });
    },
    [availableTrendMetricKeys],
  );
  const handleClearTrendMetricSelection = useCallback(() => {
    setSelectedTrendMetricKeys(null);
  }, []);
  // The empty state stands in for the recording-mode picker, so it lists only
  // the recordings still owed — questionnaires are not what this screen is
  // prompting for.
  const recordingTaskBoard = useMemo(() => {
    const recordingTasks = Array.isArray(taskBoard?.tasks)
      ? taskBoard.tasks.filter((task) => task.kind === "recording")
      : [];

    return { ...(taskBoard || {}), tasks: recordingTasks };
  }, [taskBoard]);
  // Deliberately gated on real tasks rather than hasTaskBoardContent: an
  // unassigned or error board would render a state card here, and the mode
  // tiles are the more useful thing to fall back to.
  const showTaskBoard = recordingTaskBoard.tasks.length > 0;
  const canFetchMoreResults =
    !loadingModes &&
    !recordingAccessError &&
    permittedVariants.length > 0 &&
    measurementResults.hasMore &&
    !measurementResults.loading &&
    !measurementResults.loadingMore &&
    !showLoadMoreError &&
    lastScrollLoadRowsLength !== rows.length;
  const fetchMoreResults = useCallback(() => {
    if (!canFetchMoreResults) {
      return;
    }

    setLastScrollLoadRowsLength(rows.length);
    setPageSize((currentPageSize) => currentPageSize + RESULTS_PAGE_SIZE);
  }, [canFetchMoreResults, rows.length]);
  const handleResultsScroll = useCallback(
    (event) => {
      const element = event.currentTarget;
      const distanceFromBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight;

      if (distanceFromBottom <= RESULTS_SCROLL_LOAD_MORE_THRESHOLD_PX) {
        fetchMoreResults();
      }
    },
    [fetchMoreResults],
  );
  const handleLoadMoreRetry = useCallback(() => {
    setLoadMoreRetryKey((currentRetryKey) => currentRetryKey + 1);
  }, []);

  if (loadingModes) {
    return (
      <ResultsListSkeleton
        t={t}
        message={
          showLoadingMessage ? t("regionalBackend.loading.resultsAccess") : null
        }
      />
    );
  }

  if (recordingAccessError) {
    return (
      <div className="w-full max-w-lg p-4">
        <section className="rounded-2xl border border-greyscale-6 bg-greyscale-8/40 p-4 text-left">
          <p className="subtitle-text mb-2">
            {t("regionalBackend.resultsAccess.title")}
          </p>
          <p className="muted-text">
            {recordingAccessError === "fetch_failed"
              ? t("regionalBackend.resultsAccess.fetchFailed")
              : t("regionalBackend.resultsAccess.notFound")}
          </p>
          <RegionalSupportCodeNotice
            supportCode={getRegionalSupportCode(
              recordingAccessError === "fetch_failed"
                ? "RESULTS_ACCESS_FETCH_FAILED"
                : "RESULTS_ACCESS_NOT_FOUND",
            )}
          />
        </section>
      </div>
    );
  }

  if (!permittedVariants.length) {
    return (
      <div className="muted-text p-4">
        {t("results.noneAvailable", "No results available.")}
      </div>
    );
  }

  return (
    <div
      data-testid="results-scroll-container"
      onScroll={handleResultsScroll}
      className="w-full max-w-lg h-full min-h-0 flex flex-col gap-4 flex-1 overflow-y-auto overscroll-contain"
    >
      <section className="w-full flex-1 min-h-0 flex flex-col gap-4">
        {showTrendCardsSection ? (
          <ResultsTrendCardsSection
            cards={trendCards}
            loading={trendMeasurementResults.loading}
            error={trendMeasurementResults.error}
            metrics={trendMetricOptions}
            selectedMetricKeys={selectedVisibleTrendMetricKeys}
            hasCustomSelection={hasCustomTrendSelection}
            onToggleMetric={handleToggleTrendMetric}
            onClearSelection={handleClearTrendMetricSelection}
            xDomainOverride={trendChartDomain}
          />
        ) : null}

        {showEmpty ? (
          <div className="flex w-full flex-col items-start gap-4 pb-3 pt-1 px-4 text-left">
            <div className="flex w-full flex-col gap-1 text-left">
              <p className="subtitle-text text-left">
                {t("results.empty", "No results yet.")}
              </p>
              <p className="text-sm text-left">
                {t("results.emptyCta", "Take a recording now.")}
              </p>
            </div>

            {/* Before any results exist, the outstanding tasks are the more
                useful prompt than a bare mode picker — but the board can be
                empty, so keep the tiles as the fallback rather than leaving
                the empty state with nothing to act on. */}
            {showTaskBoard ? (
              <ProtocolTaskList
                board={recordingTaskBoard}
                className="!bg-transparent !shadow-none !rounded-none !p-0"
                tasksClassName="gap-1"
                groupedRows
                emphasizeCounts
                hideHeader
              />
            ) : (
              <section className="w-full flex flex-col gap-2">
                <RecordingModeTiles
                  firstRunByVariant={firstRunByVariant}
                  isPermitted={isPermitted}
                  layout="grid"
                  spanFirstWhenOdd
                />
              </section>
            )}
          </div>
        ) : (
          <section className="w-full h-full min-h-fit flex flex-col gap-2 rounded-t-3xl bg-greyscale-1 p-4">
            <div className="flex w-full items-center justify-between gap-3 ">
              <h3 className="subtitle-text text-left ">
                {t("results.historyTitle", "History")}
              </h3>

              {permittedResultModes.length > 1 ? (
                <ResultsModeFilterButton
                  buttonLabel={t("results.filter.buttonLabel", "Filter")}
                  clearLabel={t("results.filter.clear", "Show all")}
                  emptyLabel={t(
                    "results.filter.empty",
                    "No recording modes available.",
                  )}
                  modes={permittedResultModes}
                  selectedVariants={selectedPermittedVariants}
                  onToggleVariant={handleToggleResultVariant}
                  onClear={handleClearResultFilters}
                />
              ) : null}
            </div>
            {rows.map((item) => (
              <ResultCard
                key={item.id}
                icon={item.icon}
                title={item.label}
                meta={[item.secondaryMeta, item.meta].filter(Boolean).join(" ")}
                to={item.to}
                className="gap-2 px-3 py-2.5"
              >
                {item.details?.chips?.length ||
                item.details?.messages?.length ? (
                  <div className="flex w-full flex-col gap-1.5">
                    {item.details.chips?.length ? (
                      <div className="flex w-full flex-wrap items-center gap-1.5">
                        {item.details.chips}
                      </div>
                    ) : null}
                    {/* Guidance messages always render as a block below the chips,
                        never interleaved with them. */}
                    {item.details.messages?.length ? (
                      <div className="flex w-full flex-col gap-0.5">
                        {item.details.messages}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </ResultCard>
            ))}

            {showInitialLoading
              ? Array.from({ length: RESULTS_LOADING_SKELETON_COUNT }).map(
                  (_, index) => (
                    <CompletedActivityCardSkeleton
                      key={`loading-${index}`}
                      showDetails={index < 2}
                    />
                  ),
                )
              : null}

            {measurementResults.loadingMore && rows.length > 0
              ? Array.from({ length: 2 }).map((_, index) => (
                  <CompletedActivityCardSkeleton
                    key={`loading-more-${index}`}
                    showDetails={index === 0}
                  />
                ))
              : null}

            {showFilteredEmpty ? (
              <p className="muted-text text-center">
                {t(
                  "results.filter.noMatches",
                  "No results match the current filter.",
                )}
              </p>
            ) : null}

            {showListError ? (
              <p className="muted-text text-center">
                {t(
                  "results.unavailable",
                  "We couldn't load your results right now.",
                )}
              </p>
            ) : null}

            {showLoadMoreError ? (
              <p className="muted-text text-center">
                {t(
                  "results.loadMoreUnavailable",
                  "We couldn't load more results right now.",
                )}
              </p>
            ) : null}

            {showLoadMoreError ? (
              <button
                type="button"
                className="outlined-button w-full"
                disabled={measurementResults.loadingMore}
                onClick={handleLoadMoreRetry}
              >
                {measurementResults.loadingMore
                  ? t("common.loading", "Loading...")
                  : t("results.retry", "Try again")}
              </button>
            ) : null}
          </section>
        )}
      </section>
    </div>
  );
}

export default memo(ResultsPage);
